import { datadogLogs } from '@datadog/browser-logs';
import type { v2 } from '@deque/billing-service-client';
import Keycloak from 'keycloak-js';
import React, {
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactElement
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  analyticsEvent,
  clearProgrammaticSession,
  getProgrammaticSession,
  getSSOConfig,
  getUser,
  type User
} from '../api-client';
import {
  HubSpotProperties,
  pendingPostAuthRedirectStorageKey,
  postAuthRedirectQueryParam
} from '../constants';
import billingV2Client from '../utils/billing-client/client-v2';
import setAuthComplete from '../utils/set-auth-complete';
import { isValidRedirectURI } from '../utils/validate-redirect';

// We must declare it globally and in this form, otherwise webpack's DefinePlugin will ignore it
declare let STATIC_SSO_FILENAME_WILL_BE_REPLACED_BY_WEBPACK: string;

export interface AuthUser extends User {
  logout: (options?: { redirectUri: string }) => void;
}

export const LOGIN_INITIATED_FLAG_KEY = 'loginInitiated';

export interface AuthTokens {
  access_token: string;
  refresh_token: string;
  id_token: string;
}

export interface AuthState {
  user?: AuthUser;
  loading: boolean;
  error: string;
  checkForUser: () => Promise<void>;
  refreshUser: () => Promise<User | void>;
  isAuthenticated: boolean;
  login: (redirect_uri?: string) => void;
  createAccountUrl: (() => string) | null;
  billingUser?: v2.User;
  updateBillingUser: () => Promise<v2.User | void>;
  authenticateWithTokens: (tokens: AuthTokens) => Promise<void>;
}

export interface AuthenticatedState extends Omit<AuthState, 'user'> {
  user: AuthUser;
}

export interface AuthProviderProps {
  children: React.ReactNode;
  initialUser?: AuthUser;
  initialBillingUser?: v2.User;
  initialKeycloak?: Keycloak | null;
  initialError?: string;
  initialLoading?: boolean;
  isOnPrem?: boolean;
}

const defaultContext = {
  loading: true,
  error: '',

  checkForUser: /* istanbul ignore next */ () => Promise.resolve(),
  refreshUser: /* istanbul ignore next */ () => Promise.resolve(),
  isAuthenticated: false,
  login: /* istanbul ignore next */ () => Promise.resolve(),
  createAccountUrl: /* istanbul ignore next */ () => '',
  updateBillingUser: /* istanbul ignore next */ () => Promise.resolve(),
  authenticateWithTokens: /* istanbul ignore next */ () => Promise.resolve()
};

const AuthContext = React.createContext<AuthState>(defaultContext);
const { Provider } = AuthContext;

const AuthProvider = ({
  children,
  initialUser,
  initialBillingUser,
  initialKeycloak = null,
  initialError = defaultContext.error,
  isOnPrem = false,
  initialLoading = defaultContext.loading
}: AuthProviderProps): ReactElement => {
  const { t } = useTranslation();
  const keycloak = useRef<Keycloak | null>(initialKeycloak);
  const [user, setUser] = useState<AuthUser | undefined>(initialUser);
  const [v2BillingUser, setV2BillingUser] = useState<v2.User | undefined>(
    initialBillingUser
  );
  const [loading, setLoading] = useState(initialLoading);
  const [error, setError] = useState<string>(initialError);

  useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      if (e.data.message !== 'extension:logout') {
        return;
      }

      keycloak.current?.logout();
    };

    window.addEventListener('message', onMessage);

    // If we already have a `user` (eg provided by tests), there's no need to re-fetch the data.
    if (user) {
      setLoading(false);
    } else {
      checkForUser().then(() => {
        setLoading(false);
      });
    }
    return () => {
      window.removeEventListener('message', onMessage);
    };
  }, []);

  React.useEffect(() => {
    if (!user) {
      return;
    }

    datadogLogs.setUser({ id: user.id });
    analyticsEvent(user.token, HubSpotProperties.AXE_KEYCLOAK_ID, user.id);
    analyticsEvent(
      user.token,
      HubSpotProperties.AXE_LAST_LOGIN_DATE,
      new Date().setUTCHours(0, 0, 0, 0).toString(),
      'REPLACE'
    );
  }, [user]);

  const updateV2BillingUser = async () => {
    const token = keycloak.current?.token;

    // Without a token, we cannot make any API calls. The token is _required_!
    if (!token) {
      return;
    }

    try {
      const updatedV2BillingUser = await billingV2Client.me.get(token);
      setV2BillingUser(updatedV2BillingUser);
      return updatedV2BillingUser;
    } catch {
      // silently fail
    }
    /* istanbul ignore next  */
    return;
  };

  const refreshUser = async (): Promise<User | void> => {
    const token = keycloak.current?.token;
    // Without a token, we cannot make any API calls. The token is _required_!
    if (!token) {
      return;
    }

    const currentUser = await getUser(token);

    setUser({
      ...currentUser,
      token,
      logout: (...args) => {
        clearProgrammaticSession().catch(() => {
          /* best effort */
        });
        window.postMessage({ message: 'app:logout' }, '*');
        keycloak.current?.logout(...args);
      }
    });

    return currentUser;
  };

  const handleSetUser = async () => {
    const currentUser = await refreshUser();
    if (!currentUser) {
      return;
    }

    try {
      // Update/get the v2 billing user, if one exists
      await updateV2BillingUser();
    } catch {
      // silently fail
    }

    // The axe extensions creates a new tab for authentication purposes and
    // looks for an `authComplete` param to determine when it's safe to onClose
    // the created tab
    const params = new URLSearchParams(window.location.search);
    if (params.get('fromextension')) {
      // When on-prem, we don't show the `<FirstLoginAlert>`, and therefore, never write an entry to the accounts table for the user.
      if (!isOnPrem) {
        // A user receives an entry in the "accounts" table after confirming our Ts and Cs. If `hasAccountEntry` is false, the user has not yet confirmed the Ts and Cs and therefore, the alert is still open.
        const isAlertOpen = !currentUser.hasAccountEntry;
        if (isAlertOpen) {
          return;
        }
      }
      setTimeout(setAuthComplete, 100);
    }
  };

  const attachKeycloakHandlers = (kc: Keycloak) => {
    kc.onAuthRefreshSuccess = async () => {
      await refreshUser();
    };

    kc.onTokenExpired = async () => {
      await keycloak.current?.updateToken(-1);
    };

    kc.onAuthSuccess = () => {
      if (!kc.tokenParsed) {
        return;
      }

      const { preferred_username, idp = 'email' } = kc.tokenParsed;

      // @ts-expect-error - Reo gets added to the window object by a script
      const reo = window.Reo;
      if (reo) {
        reo.identify({
          username: preferred_username,
          type: idp
        });
      }
    };
  };

  // instantiates and initializes keycloak (and updates the keycloak ref).
  // First tries the programmatic-session HttpOnly cookie (set by
  // /api/invitation-signup) and inits keycloak-js with those tokens. If the
  // cookie is absent or those tokens are no longer valid (e.g. expired or
  // already-rotated refresh token), falls back to the standard check-sso
  // redirect-cookie flow.
  const initKeycloak = async (): Promise<boolean> => {
    const authConfig = await getSSOConfig();
    const makeKeycloak = () =>
      new Keycloak({
        realm: authConfig.realm,
        url: `${authConfig.url}/auth`,
        clientId: authConfig.publicClientId
      });
    let serverPath = window.appConfig.PUBLIC_PATH;
    // Public path is an empty slash when running in a localhost mode, or a full URL (to CDN) when running on a server
    // in localhost it's '/'
    // in dev server it's https://d1yaj4ub9j1jia.cloudfront.net/assets/
    // Keycloak require this to be an absolute URL, so have to tweak it to be absolute, when we are running in a localhost mode
    if (serverPath === '/') {
      // This will execute in dev mode only
      serverPath = window.location.origin + '/';
    } else {
      // This will execute on the server deploy
      serverPath = window.location.origin + '/assets/';
    }
    const silentCheckSsoRedirectUri = new URL(
      STATIC_SSO_FILENAME_WILL_BE_REPLACED_BY_WEBPACK,
      serverPath
    );

    let tokens: AuthTokens | null = null;
    try {
      tokens = await getProgrammaticSession();
    } catch {
      tokens = null;
    }

    if (tokens) {
      const kcFromTokens = makeKeycloak();
      attachKeycloakHandlers(kcFromTokens);
      try {
        const authenticated = await kcFromTokens.init({
          token: tokens.access_token,
          refreshToken: tokens.refresh_token,
          idToken: tokens.id_token,
          checkLoginIframe: false,
          timeSkew: 0
        });
        if (authenticated && kcFromTokens.authenticated) {
          keycloak.current = kcFromTokens;
          return true;
        }
      } catch {
        // fall through to check-sso
      }
      // Cookie tokens were not usable — drop them so we don't retry on every load.
      try {
        await clearProgrammaticSession();
      } catch {
        // best effort
      }
    }

    const kc = makeKeycloak();
    attachKeycloakHandlers(kc);

    const authenticated = await kc.init({
      onLoad: 'check-sso',
      checkLoginIframe: false,
      silentCheckSsoRedirectUri: silentCheckSsoRedirectUri.toString(),
      timeSkew: 0
    });
    keycloak.current = kc;
    return authenticated;
  };

  // Checks for user by initializing keycloak, checking for an existing token
  // (which indicates an already logged in user), and setting the user if one
  // exists. NOTE: the user will NOT be redirected to the login page if not
  // authenticated - use `authenticate` for that functionality
  const checkForUser = async () => {
    try {
      if (!keycloak.current) {
        await initKeycloak();
      }
      await handleSetUser();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log(err);
      setError(t('Failed to initialize auth'));
    }
  };

  const buildDefaultLoginRedirectUri = (): string => {
    const pendingRedirect = sessionStorage.getItem(
      pendingPostAuthRedirectStorageKey
    );
    if (!pendingRedirect || !isValidRedirectURI(pendingRedirect)) {
      return window.location.href;
    }
    const url = new URL(window.location.href);
    url.searchParams.set(postAuthRedirectQueryParam, pendingRedirect);
    return url.toString();
  };

  const login = (redirectUri?: string) => {
    // we use this key to track the login flow when redirected back from keycloak and trigger login analytics event
    window.localStorage.setItem(LOGIN_INITIATED_FLAG_KEY, 'true');
    keycloak.current?.login({
      redirectUri: redirectUri ?? buildDefaultLoginRedirectUri()
    });
  };

  const authenticateWithTokens = async (tokens: AuthTokens) => {
    const authConfig = await getSSOConfig();
    const kc = new Keycloak({
      realm: authConfig.realm,
      url: `${authConfig.url}/auth`,
      clientId: authConfig.publicClientId
    });

    attachKeycloakHandlers(kc);

    const authenticated = await kc.init({
      token: tokens.access_token,
      refreshToken: tokens.refresh_token,
      idToken: tokens.id_token,
      checkLoginIframe: false,
      timeSkew: 0
    });

    if (!authenticated || !kc.authenticated) {
      throw new Error('Failed to authenticate with provided tokens');
    }

    if (!kc.token || !kc.refreshToken || !kc.idToken) {
      throw new Error('Failed to authenticate with provided tokens');
    }

    keycloak.current = kc;
    await handleSetUser();
  };

  return (
    <Provider
      value={{
        user,
        loading,
        error,
        checkForUser,
        refreshUser,
        isAuthenticated: !!user?.id,
        login,
        createAccountUrl: keycloak.current && keycloak.current.createAccountUrl,
        billingUser: v2BillingUser,
        updateBillingUser: updateV2BillingUser,
        authenticateWithTokens
      }}
    >
      {children}
    </Provider>
  );
};

const useAuthContext = (): AuthState => useContext(AuthContext);

// A hook that ensures 'user' is defined, intended for use in protected routes when the user is guaranteed to be authenticated and loaded.
const useAuthenticatedContext = (): AuthenticatedState => {
  const context = useAuthContext();

  if (context.loading) {
    throw new Error(
      'useAuthenticatedContext was used in a context where the user is not yet loaded.'
    );
  }

  if (!context.user) {
    throw new Error(
      'useAuthenticatedContext was used in a context where the user is not authenticated.'
    );
  }

  return context as AuthenticatedState;
};

export { AuthProvider, useAuthContext, useAuthenticatedContext };
